import React from "react";
import { toast } from "sonner";
import { FileDown, Share } from "lucide-react";
import {
  SeriesConfig,
  AxisConfig,
  TabConfig,
} from "@/components/HorizontalStackedBarChart/types";
import { TFunction } from "i18next";
import { getGroupNames, FunnelMapKeys } from "@/util/funnelGroups";
import { PredictiveModelType } from "../types";
import { FunnelStateEnum } from "@/__generated__/types";

const CHART_TAB_KEYS = {
  ADMITS_ONLY: "admitsOnly",
  APPLICANTS_ONLY: "applicantsOnly",
  ALL_STUDENTS: "allStudents",
} as const;

const CHART_SERIES_KEYS = {
  COMMITTED: "committed",
  NOT_COMMITTED: "notCommitted",
  APPLIED: "applied",
  NOT_APPLIED: "notApplied",
} as const;

type ChartTabKey = (typeof CHART_TAB_KEYS)[keyof typeof CHART_TAB_KEYS];

export { CHART_TAB_KEYS, CHART_SERIES_KEYS };
export type { ChartTabKey };

export const getFunnelFilterValue = (
  tab: ChartTabKey,
  committed: boolean,
): FunnelMapKeys<"SIDEBAR">[] => {
  const committedAndEnrolled: FunnelMapKeys<"SIDEBAR">[] = [
    "committed",
    "enrolled",
  ];
  if (committed) return committedAndEnrolled;

  if (tab === CHART_TAB_KEYS.ADMITS_ONLY) {
    return ["admitted", "acceptedDeclined", "commitmentWithdrawn"];
  }

  return getGroupNames("SIDEBAR").filter(
    (state) => !committedAndEnrolled.includes(state),
  );
};

export const getRiseFunnelStates = (
  modelType: PredictiveModelType,
  tab: ChartTabKey | "default",
  committed: boolean,
): FunnelStateEnum[] => {
  const isApplyIndex = modelType === "Apply";

  if (isApplyIndex && tab === "default") {
    if (committed) {
      return [
        FunnelStateEnum.Applied,
        FunnelStateEnum.ApplicationComplete,
        FunnelStateEnum.ApplicationCompleteWithdrawn,
        FunnelStateEnum.Denied,
        FunnelStateEnum.Waitlisted,
        FunnelStateEnum.WaitlistedWithdrawn,
        FunnelStateEnum.Deferred,
        FunnelStateEnum.DeferredWithdrawn,
        FunnelStateEnum.Accepted,
        FunnelStateEnum.AcceptanceDeclined,
        FunnelStateEnum.Committed,
        FunnelStateEnum.CommitmentWithdrawn,
        FunnelStateEnum.Enrolled,
      ];
    }

    return [
      FunnelStateEnum.Lead,
      FunnelStateEnum.LeadDropped,
      FunnelStateEnum.Inquired,
      FunnelStateEnum.InquiredDropped,
      FunnelStateEnum.ApplicationStarted,
      FunnelStateEnum.ApplicationStartedCanceled,
      FunnelStateEnum.ApplicationCanceled,
    ];
  }

  if (committed) {
    return [FunnelStateEnum.Committed, FunnelStateEnum.Enrolled];
  }

  if (tab === CHART_TAB_KEYS.ADMITS_ONLY) {
    return [
      FunnelStateEnum.Accepted,
      FunnelStateEnum.AcceptanceDeclined,
      FunnelStateEnum.CommitmentWithdrawn,
    ];
  }

  if (tab === CHART_TAB_KEYS.APPLICANTS_ONLY) {
    return [
      FunnelStateEnum.Applied,
      FunnelStateEnum.ApplicationComplete,
      FunnelStateEnum.ApplicationCompleteWithdrawn,
      FunnelStateEnum.Denied,
      FunnelStateEnum.Waitlisted,
      FunnelStateEnum.WaitlistedWithdrawn,
      FunnelStateEnum.Deferred,
      FunnelStateEnum.DeferredWithdrawn,
      FunnelStateEnum.Accepted,
      FunnelStateEnum.AcceptanceDeclined,
      FunnelStateEnum.CommitmentWithdrawn,
    ];
  }

  return [
    FunnelStateEnum.Lead,
    FunnelStateEnum.LeadDropped,
    FunnelStateEnum.Inquired,
    FunnelStateEnum.InquiredDropped,
    FunnelStateEnum.ApplicationStarted,
    FunnelStateEnum.ApplicationStartedCanceled,
    FunnelStateEnum.ApplicationCanceled,
    FunnelStateEnum.Applied,
    FunnelStateEnum.ApplicationComplete,
    FunnelStateEnum.ApplicationCompleteWithdrawn,
    FunnelStateEnum.Denied,
    FunnelStateEnum.Waitlisted,
    FunnelStateEnum.WaitlistedWithdrawn,
    FunnelStateEnum.Deferred,
    FunnelStateEnum.DeferredWithdrawn,
    FunnelStateEnum.Accepted,
    FunnelStateEnum.AcceptanceDeclined,
    FunnelStateEnum.CommitmentWithdrawn,
  ];
};

export const chartConfig = (
  t: TFunction,
  modelType?: PredictiveModelType,
  isRiseEnabled?: boolean,
): Record<string, { label: string; color: string }> => {
  const isApplyIndex = isRiseEnabled && modelType === "Apply";
  const config: Record<string, { label: string; color: string }> = {
    [CHART_SERIES_KEYS.COMMITTED]: {
      label: t("predictiveModels.chart.commitmentType.committed"),
      color: "var(--color-horizontal-bar-chart-dark)",
    },
    [CHART_SERIES_KEYS.NOT_COMMITTED]: {
      label: t("predictiveModels.chart.commitmentType.notCommitted"),
      color: "var(--color-horizontal-bar-chart-light)",
    },
  };

  if (isApplyIndex) {
    config[CHART_SERIES_KEYS.APPLIED] = {
      label: t("predictiveModels.chart.commitmentType.applied"),
      color: "var(--color-horizontal-bar-chart-dark)",
    };
    config[CHART_SERIES_KEYS.NOT_APPLIED] = {
      label: t("predictiveModels.chart.commitmentType.notApplied"),
      color: "var(--color-horizontal-bar-chart-light)",
    };
  }

  return config;
};

export const seriesConfig = (
  t: TFunction,
  modelType?: PredictiveModelType,
  isRiseEnabled?: boolean,
): SeriesConfig[] => {
  const isApplyIndex = isRiseEnabled && modelType === "Apply";

  if (isApplyIndex) {
    return [
      {
        key: CHART_SERIES_KEYS.APPLIED,
        label: t("predictiveModels.chart.commitmentType.applied"),
        color: "var(--color-horizontal-bar-chart-dark)",
      },
      {
        key: CHART_SERIES_KEYS.NOT_APPLIED,
        label: t("predictiveModels.chart.commitmentType.notApplied"),
        color: "var(--color-horizontal-bar-chart-light)",
      },
    ];
  }

  return [
    {
      key: CHART_SERIES_KEYS.COMMITTED,
      label: t("predictiveModels.chart.commitmentType.committed"),
      color: "var(--color-horizontal-bar-chart-dark)",
    },
    {
      key: CHART_SERIES_KEYS.NOT_COMMITTED,
      label: t("predictiveModels.chart.commitmentType.notCommitted"),
      color: "var(--color-horizontal-bar-chart-light)",
    },
  ];
};

export const axisConfig = (t: TFunction): AxisConfig => ({
  categoryKey: "level",
  categoryLabel: undefined,
  valueLabel: t("predictiveModels.chart.xAxis"),
});

export const tabsConfig = (
  t: TFunction,
  modelType?: PredictiveModelType,
  isRiseEnabled?: boolean,
): TabConfig<ChartTabKey>[] => {
  const isApplyIndex = isRiseEnabled && modelType === "Apply";

  if (isApplyIndex) {
    return [];
  }

  if (isRiseEnabled) {
    return [
      {
        label: t("predictiveModels.chart.tabs.admitsOnly"),
        value: CHART_TAB_KEYS.ADMITS_ONLY,
      },
      {
        label: t("predictiveModels.chart.tabs.applicantsOnly"),
        value: CHART_TAB_KEYS.APPLICANTS_ONLY,
      },
      {
        label: t("predictiveModels.chart.tabs.allStudents"),
        value: CHART_TAB_KEYS.ALL_STUDENTS,
      },
    ];
  }

  return [
    {
      label: t("predictiveModels.chart.tabs.admitsOnly"),
      value: CHART_TAB_KEYS.ADMITS_ONLY,
    },
    {
      label: t("predictiveModels.chart.tabs.allStudents"),
      value: CHART_TAB_KEYS.ALL_STUDENTS,
    },
  ];
};

export const menuOptions = (t: TFunction) => {
  return [
    {
      label: t("global.exportToCSV.menuTitle"),
      onClick: () => toast("Exporting to CSV..."),
      icon: <FileDown aria-hidden height={16} />,
    },
    /* -- Hide for now
    {
      label: t(
        "predictiveModels.chart.menuOptions.exportAsImage",
      ),
      disabled: true,
      onClick: () => toast("Exporting as Image..."),
      icon: <ImageDown aria-hidden height={16} />,
    },
    */
    {
      label: t("predictiveModels.chart.menuOptions.share"),
      onClick: () => {
        toast.success(t("predictiveModels.chart.menuOptions.successCopied"));
        navigator.clipboard.writeText(window.location.href);
      },
      icon: <Share aria-hidden height={16} />,
    },
  ];
};
